import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type * as Y from 'yjs';
import { MAX_MESSAGE_LENGTH, type ChatMessage, type ClipCard } from '../../crdt/chat';
import {
  readProposal,
  observeAgent,
  readStatus,
  readTrace,
  STATUS_STALE_MS,
  type ProposalRecord,
} from '../../crdt/agent';
import { useChat } from '../../state/chat';
import { useAgent, type RunNotice } from '../../state/agent';
import { archiveByKey, type Archive } from '../../api/archives';
import type { ItemPreset } from '../ItemDetail';
import { addClipCardToBin, addClipCardToTimeline } from './clipActions';
import ClipCardPreview from './ClipCardPreview';
import { usePresence } from '../../state/presence';
import { useProject } from '../../state/project';
import { getTimelineDoc } from '../../state/timeline';
import ProposalCard from './ProposalCard';
import { LiveStepList, TraceRow } from './StepList';
import './ChatPanel.css';

/**
 * The steps of a finished run, read from the agent root. Re-reads on `rev` the
 * same way proposal cards do, so a trace arriving from a collaborator appears
 * without a reload.
 */
function RunTrace({ doc, runId, rev }: { doc: Y.Doc | null; runId: string; rev: number }) {
  const trace = useMemo(() => (doc ? readTrace(doc, runId) : null), [doc, runId, rev]);
  if (!trace) return null;
  return <TraceRow trace={trace} />;
}

/** Distance from the bottom within which we consider the user "pinned". */
const PIN_SLACK_PX = 40;

function timeLabel(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Collaborator chat for the current project.
 *
 * Messages live in the shared document, so history is whatever the room has —
 * anyone who opens the project sees the conversation, including people who join
 * later. The AI assistant posts here too (`kind: 'agent'`), which is why its
 * replies, clip strips and proposal cards are visible to every collaborator even
 * though only staff can invoke it.
 */
export interface ChatPanelProps {
  archives?: Archive[];
  /** Open an item in the bin's detail view, as the advanced search does. */
  onPreviewClip?: (archiveKey: string, itemId: string, preset: ItemPreset) => void;
}

export default function ChatPanel({ archives, onPreviewClip }: ChatPanelProps = {}) {
  const messages = useChat((s) => s.messages);
  const send = useChat((s) => s.send);
  const self = usePresence((s) => s.self);
  // The chat/agent roots live on the timeline document. It is REBUILT on every
  // connect (initEmptyDocForSync), so re-derive it whenever the connection
  // changes rather than capturing a doc that may already be dead.
  const connection = useProject((s) => s.connection);
  const doc = useMemo<Y.Doc>(() => getTimelineDoc(), [connection]);

  const agentEnabled = useAgent((s) => s.status.enabled);
  const assistantMode = useAgent((s) => s.assistantMode);
  const setAssistantMode = useAgent((s) => s.setAssistantMode);
  const semantic = useAgent((s) => s.semantic);
  const semanticMode = useAgent((s) => s.semanticMode);
  const setSemanticMode = useAgent((s) => s.setSemanticMode);
  const running = useAgent((s) => s.running);
  const streamingText = useAgent((s) => s.streamingText);
  const steps = useAgent((s) => s.steps);
  const liveRunId = useAgent((s) => s.runId);
  const notice = useAgent((s) => s.notice);
  const remaining = useAgent((s) => s.status.remaining);
  const ask = useAgent((s) => s.ask);
  const stop = useAgent((s) => s.stop);
  const retry = useAgent((s) => s.retry);
  const dismissNotice = useAgent((s) => s.dismissNotice);

  const [draft, setDraft] = useState('');
  // Bumped on any agent-root change so proposal cards re-read their record.
  const [agentRev, setAgentRev] = useState(0);
  // Bumped only to re-evaluate a remote run's staleness (see below).
  const [, setStaleTick] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);

  useEffect(() => {
    if (!doc) return;
    return observeAgent(doc, () => setAgentRev((v) => v + 1));
  }, [doc]);

  // Follow new messages, but don't yank the view if they've scrolled back to
  // read something.
  useEffect(() => {
    const el = listRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, streamingText, steps]);

  function onScroll() {
    const el = listRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < PIN_SLACK_PX;
  }

  function submit() {
    const body = draft.trim();
    if (!body) return;
    if (assistantMode && agentEnabled && !outOfBudget) {
      if (running) return;
      void ask(body);
      setDraft('');
      pinnedRef.current = true;
      return;
    }
    // Keep the draft if the document is not attached (mid-reconnect): losing
    // what someone just typed with no explanation is worse than a stuck send.
    if (!send(body)) return;
    setDraft('');
    pinnedRef.current = true;
  }

  // The server reports what is left of today's budget; with none left the
  // toggle is disabled rather than letting the user spend a turn discovering it.
  const outOfBudget = agentEnabled && remaining !== undefined && remaining <= 0;

  // A run can post several messages — a clip strip, a proposal card, then its
  // reply — and they all carry the same runId. Anchor the trace to the LAST of
  // them so it appears once, at the end of the run's block. The user's own
  // message carries the runId too, which is what makes a run that answered with
  // nothing still show its steps.
  const traceAnchors = useMemo(() => {
    const last = new Map<string, number>();
    messages.forEach((m, i) => {
      if (m.runId) last.set(m.runId, i);
    });
    return last;
  }, [messages]);

  // Someone else's run, so this client shows a status line rather than a stream.
  const remoteStatus = doc ? readStatus(doc, Date.now()) : null;
  const remoteWorking =
    !running && remoteStatus?.state === 'running' && remoteStatus.byName !== self?.name;

  // `readStatus` ages an abandoned run out, but a dead client sends nothing
  // more — so no document change will ever arrive to re-render the banner away,
  // and it would sit there until something unrelated happened to repaint.
  // Schedule exactly one wake-up, for the moment it goes stale.
  const remoteStartedAt = remoteWorking ? remoteStatus?.at : undefined;
  useEffect(() => {
    if (remoteStartedAt === undefined) return;
    const dueIn = remoteStartedAt + STATUS_STALE_MS - Date.now();
    const timer = setTimeout(() => setStaleTick((n) => n + 1), Math.max(0, dueIn) + 250);
    return () => clearTimeout(timer);
  }, [remoteStartedAt]);

  return (
    <div className="chat-panel">
      <div
        className="chat-list"
        ref={listRef}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Messages"
      >
        {messages.length === 0 && !running ? (
          <p className="search-hint">
            no messages yet — say hello to whoever else is editing
          </p>
        ) : (
          messages.map((m, i) => (
            <Fragment key={m.id}>
              <Message
                message={m}
                isSelf={!!self && m.author.name === self.name}
                doc={doc}
                rev={agentRev}
                archives={archives}
                onPreviewClip={onPreviewClip}
              />
              {m.runId &&
                traceAnchors.get(m.runId) === i &&
                // While the run is still going, the live trace in the pending
                // bubble below is the same thing — don't show both.
                !(running && m.runId === liveRunId) && (
                  <RunTrace doc={doc} runId={m.runId} rev={agentRev} />
                )}
            </Fragment>
          ))
        )}

        {remoteWorking && (
          <p className="chat-remote-status">
            ✨ Assistant is working (asked by {remoteStatus?.byName})
          </p>
        )}

        {running && (
          <div className="chat-msg chat-msg-agent chat-msg-pending">
            <div className="chat-msg-head">
              <span className="chat-author" style={{ color: '#a78bfa' }}>
                Assistant
              </span>
              <button className="chat-stop" onClick={stop}>
                Stop
              </button>
            </div>
            <LiveStepList steps={steps} />
            <div className="chat-body">
              {streamingText || <span className="chat-thinking">Thinking…</span>}
            </div>
          </div>
        )}

        {notice && !running && (
          <RunNoticeBanner notice={notice} onRetry={retry} onDismiss={dismissNotice} />
        )}
      </div>

      <div className="chat-compose">
        {agentEnabled && (
          <div className="chat-toggles">
            <label
              className={`chat-assistant-toggle${outOfBudget ? ' is-disabled' : ''}`}
              title={
                outOfBudget
                  ? "Today's assistant token budget is used up. It resets at 00:00 UTC."
                  : undefined
              }
            >
              <input
                type="checkbox"
                checked={assistantMode && !outOfBudget}
                disabled={outOfBudget}
                onChange={(e) => setAssistantMode(e.target.checked)}
              />
              <span>✨ Ask the assistant{outOfBudget ? ' — budget used up' : ''}</span>
            </label>
            {/* Only when the backend names a pandora-embeddings service: with
                nothing to call, the toggle would be a promise the run cannot
                keep. Off = exactly the assistant above; on = four more tools
                and a prompt section, so it costs tokens and is a choice. */}
            {semantic && (
              <label
                className="chat-assistant-toggle chat-semantic-toggle"
                title={`Also search ${semantic.archive} shot descriptions by meaning, and build arcs and drift sequences from them`}
              >
                <input
                  type="checkbox"
                  checked={semanticMode}
                  onChange={(e) => setSemanticMode(e.target.checked)}
                  aria-label="Semantic search"
                />
                <span>Semantic search</span>
              </label>
            )}
          </div>
        )}
        {/* Safe from the global keymap: it ignores events from textareas
            (keys/keymap.ts), so typing "s" here won't split a clip. */}
        <textarea
          className="chat-input"
          rows={2}
          value={draft}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder={
            assistantMode && agentEnabled
              ? 'Ask the assistant to find footage or edit…'
              : 'Message collaborators…'
          }
          aria-label={
            assistantMode && agentEnabled ? 'Ask the assistant' : 'Message collaborators'
          }
          aria-describedby="chat-send-hint"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          className="primary chat-send"
          disabled={!draft.trim() || (assistantMode && (running || outOfBudget))}
          onClick={submit}
        >
          {assistantMode && agentEnabled ? 'Ask' : 'Send'}
        </button>
        <span id="chat-send-hint" className="visually-hidden">
          Press Enter to send, Shift and Enter for a new line.
        </span>
      </div>
    </div>
  );
}

/**
 * What went wrong, in the user's terms, with Retry only where retrying could
 * actually help. The provider/proxy detail is kept but folded away — it is what
 * makes a bug report useful without making the panel shout.
 */
function RunNoticeBanner({
  notice,
  onRetry,
  onDismiss,
}: {
  notice: RunNotice;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const [showDetail, setShowDetail] = useState(false);
  return (
    <div className={`chat-notice chat-notice-${notice.kind}`} role="status">
      <span className="chat-notice-text">{notice.message}</span>
      <span className="chat-notice-actions">
        {notice.retryable && (
          <button className="chat-notice-btn" onClick={onRetry}>
            Retry
          </button>
        )}
        {notice.detail && notice.detail !== notice.message && (
          <button className="chat-notice-btn" onClick={() => setShowDetail((v) => !v)}>
            {showDetail ? 'Less' : 'Details'}
          </button>
        )}
        <button className="chat-notice-btn" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      </span>
      {showDetail && notice.detail && <pre className="chat-notice-detail">{notice.detail}</pre>}
    </div>
  );
}

function Message({
  message,
  isSelf,
  doc,
  rev,
  archives,
  onPreviewClip,
}: {
  message: ChatMessage;
  isSelf: boolean;
  doc: Y.Doc;
  rev: number;
  archives?: Archive[];
  onPreviewClip?: (archiveKey: string, itemId: string, preset: ItemPreset) => void;
}) {
  return (
    <div className={`chat-msg chat-msg-${message.kind}`}>
      <div className="chat-msg-head">
        <span className="chat-author" style={{ color: message.author.color }}>
          {message.author.name}
          {isSelf ? ' (you)' : ''}
        </span>
        <span className="chat-time">{timeLabel(message.at)}</span>
      </div>
      {message.body && <div className="chat-body">{message.body}</div>}
      {message.atts?.map((att, i) => {
        if (att.type === 'clips') {
          return (
            <ClipStrip
              key={i}
              clips={att.clips}
              archives={archives}
              onPreviewClip={onPreviewClip}
            />
          );
        }
        if (att.type === 'moments') {
          return (
            <MomentResults
              key={i}
              label={att.label}
              total={att.total}
              more={att.more}
              clips={att.clips}
              archives={archives}
              onPreviewClip={onPreviewClip}
            />
          );
        }
        return <ProposalAttachment key={i} proposalId={att.proposalId} doc={doc} rev={rev} />;
      })}
    </div>
  );
}

function ClipStrip({
  clips,
  archives,
  onPreviewClip,
}: {
  clips: ClipCard[];
  archives?: Archive[];
  onPreviewClip?: (archiveKey: string, itemId: string, preset: ItemPreset) => void;
}) {
  return (
    <ul className="clip-strip">
      {clips.map((c, i) => (
        <ClipCardView
          key={`${c.item}-${i}`}
          card={c}
          archives={archives}
          onPreview={onPreviewClip}
        />
      ))}
    </ul>
  );
}

/** Cards shown per film before "show all" appears. */
const FILM_PREVIEW = 6;

/** Above this many cards the whole result set starts collapsed. */
const COLLAPSE_ABOVE = 40;

/**
 * A whole moment search, shown in full.
 *
 * The point of this component is that nothing here decides what the user is
 * allowed to see — the assistant used to pick three of forty-seven, and the
 * three were arbitrary. So every match is present; grouping by film and
 * collapsing are the only concessions to the panel being narrow.
 *
 * Cards stay cheap while collapsed because ClipCardPreview loads a poster frame
 * and nothing else until it is clicked.
 */
function MomentResults({
  label,
  total,
  more,
  clips,
  archives,
  onPreviewClip,
}: {
  label: string;
  total: number;
  more?: boolean;
  clips: ClipCard[];
  archives?: Archive[];
  onPreviewClip?: (archiveKey: string, itemId: string, preset: ItemPreset) => void;
}) {
  const [open, setOpen] = useState(clips.length <= COLLAPSE_ABOVE);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const films = useMemo(() => {
    const by = new Map<string, { item: string; title: string; clips: ClipCard[] }>();
    for (const c of clips) {
      const hit = by.get(c.item);
      if (hit) hit.clips.push(c);
      else by.set(c.item, { item: c.item, title: c.title || c.item, clips: [c] });
    }
    return [...by.values()];
  }, [clips]);

  // `total` is exact for an ordinary search and a floor for one that hit the
  // limit — pan.do/ra has no cheap count (api/pandora.ts). Say which, rather
  // than letting a truncated result read as a complete one.
  const shortfall = total > clips.length ? ` · showing ${clips.length}` : '';
  const summary = `${more ? 'at least ' : ''}${total} moment${
    total === 1 ? '' : 's'
  } in ${films.length} film${films.length === 1 ? '' : 's'}${shortfall}${
    more ? ' · narrow the search to see the rest' : ''
  }`;

  return (
    <div className="moment-results">
      <button
        className="moment-results-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="moment-results-count">{summary}</span>
        <span className="moment-results-label">{label}</span>
      </button>
      {open &&
        films.map((film) => {
          const showAll = expanded[film.item] ?? film.clips.length <= FILM_PREVIEW;
          const shown = showAll ? film.clips : film.clips.slice(0, FILM_PREVIEW);
          return (
            <div className="moment-film" key={film.item}>
              <div className="moment-film-head">
                {film.title} <span className="moment-film-count">{film.clips.length}</span>
              </div>
              <ClipStrip clips={shown} archives={archives} onPreviewClip={onPreviewClip} />
              {!showAll && (
                <button
                  className="moment-film-more"
                  onClick={() => setExpanded((e) => ({ ...e, [film.item]: true }))}
                >
                  show all {film.clips.length} in this film
                </button>
              )}
            </div>
          );
        })}
    </div>
  );
}

function ClipCardView({
  card,
  archives,
  onPreview,
}: {
  card: ClipCard;
  archives?: Archive[];
  onPreview?: (archiveKey: string, itemId: string, preset: ItemPreset) => void;
}) {
  const [busy, setBusy] = useState<'bin' | 'timeline' | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const archive = archives ? archiveByKey(archives, card.archive) : undefined;

  async function run(kind: 'bin' | 'timeline') {
    if (!archives || busy) return;
    setBusy(kind);
    setFailed(null);
    const result =
      kind === 'bin'
        ? await addClipCardToBin(card, archives)
        : await addClipCardToTimeline(card, archives);
    setBusy(null);
    if (result.ok) {
      setDone(kind === 'bin' ? 'Saved' : 'Added');
      setTimeout(() => setDone(null), 1400);
    } else {
      setFailed(result.error);
    }
  }

  return (
    <li className="clip-card">
      <ClipCardPreview card={card} archive={archive} />
      <div className="clip-card-title" title={card.title}>
        {card.title}
      </div>
      <div className="clip-card-meta">
        {card.archive} · {clock(card.in)}–{clock(card.out)}
      </div>
      {card.note && <div className="clip-card-note">{card.note}</div>}
      <div className="clip-card-actions">
        {onPreview && (
          <button
            onClick={() =>
              onPreview(card.archive, card.item, { concatIn: card.in, concatOut: card.out })
            }
            title="Open the full item view, with transcript and marks"
          >
            Open
          </button>
        )}
        <button disabled={!archives || busy !== null} onClick={() => void run('bin')}>
          {busy === 'bin' ? '…' : '+ Bin'}
        </button>
        <button disabled={!archives || busy !== null} onClick={() => void run('timeline')}>
          {busy === 'timeline' ? '…' : '+ Timeline'}
        </button>
      </div>
      {done && <div className="clip-card-done">{done}</div>}
      {failed && <div className="clip-card-failed">{failed}</div>}
    </li>
  );
}

/**
 * Resolve a proposal id against the agent root. Re-reads whenever that root
 * changes (`rev`), so status updates from any collaborator show up live.
 */
function ProposalAttachment({
  proposalId,
  doc,
  rev,
}: {
  proposalId: string;
  doc: Y.Doc;
  rev: number;
}) {
  void rev; // re-render trigger only
  const record: ProposalRecord | null = doc ? readProposal(doc, proposalId) : null;
  if (!record) return <p className="chat-note">(proposal no longer available)</p>;
  return <ProposalCard record={record} />;
}
